/** @format */

import Skeleton from '@mui/material/Skeleton';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { viewContentDetail } from 'src/slices/topics';
import { useDispatch, useSelector } from 'src/store';
import { formatDate } from 'src/utils/helper';
import { getContentLanguage } from 'src/utils/locale';
import { getExtranetLanguageDisplayCode } from 'src/utils/extranetLanguages';
import { buildSearchUrlSearch } from 'src/utils/searchUrl';
import useDeviceDetect from 'src/hooks/useDeviceDetect';
import { GlobeIcon } from 'src/components/icons';

export const ContentItem = ({ item, onClick }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const topicTree = useSelector(state => {
    const publishedTopics = state.topics?.publishedTopics;
    if (Array.isArray(publishedTopics)) return publishedTopics;
    const allTopics = state.topics?.topicTree;
    return Array.isArray(allTopics) ? allTopics : [];
  });
  const navigate = useNavigate();
  const { isMobile } = useDeviceDetect();
  const isClickable = typeof onClick === 'function';
  // Detect if we're in administration context
  const isAdministrationContext = useMemo(() => {
    const pathname = location.pathname || window.location?.pathname || '';
    return pathname.startsWith('/administration');
  }, [location.pathname]);
  const visibleTagLimit = isMobile ? 3 : 5;
  const resolvedLanguage =
    typeof item?.language === 'string' && item.language.trim()
      ? item.language.trim().split('|')[0].trim().toLowerCase()
      : getContentLanguage();
  const topicInfo = useMemo(() => {
    const topics = Array.isArray(item?.topics) ? item.topics : [];
    const firstTopic = topics[0];
    const fallbackTopicId =
      item?.topicID !== undefined && item?.topicID !== null && String(item.topicID).trim()
        ? String(item.topicID).trim()
        : item?.topicId !== undefined && item?.topicId !== null && String(item.topicId).trim()
          ? String(item.topicId).trim()
          : null;
    if (typeof firstTopic === 'string' && firstTopic.trim()) {
      return { label: firstTopic.trim(), id: fallbackTopicId };
    }
    if (firstTopic && typeof firstTopic === 'object') {
      const label = firstTopic.displayName || firstTopic.name;
      const topicId =
        firstTopic.topicID !== undefined && firstTopic.topicID !== null && String(firstTopic.topicID).trim()
          ? String(firstTopic.topicID).trim()
          : firstTopic.topicId !== undefined && firstTopic.topicId !== null && String(firstTopic.topicId).trim()
            ? String(firstTopic.topicId).trim()
            : firstTopic.id !== undefined && firstTopic.id !== null && String(firstTopic.id).trim()
              ? String(firstTopic.id).trim()
              : fallbackTopicId;
      if (typeof label === 'string' && label.trim()) {
        return { label: label.trim(), id: topicId };
      }
    }
    return { label: '', id: fallbackTopicId };
  }, [item?.topicID, item?.topicId, item?.topics]);
  const topicTargetId = useMemo(() => {
    if (topicInfo.id) {
      return topicInfo.id;
    }
    if (!topicInfo.label) {
      return null;
    }
    const normalizedLabel = String(topicInfo.label).trim().toLowerCase();
    const queue = Array.isArray(topicTree) ? [...topicTree] : [];
    while (queue.length > 0) {
      const node = queue.shift();
      if (!node || typeof node !== 'object') continue;
      const nodeLabel = (node.displayName || node.name || '').trim().toLowerCase();
      if (nodeLabel && nodeLabel === normalizedLabel) {
        const nodeId = node.id ?? node.topicID ?? node.topicId;
        if (nodeId !== undefined && nodeId !== null && String(nodeId).trim()) {
          return String(nodeId);
        }
      }
      if (Array.isArray(node.children)) {
        queue.push(...node.children);
      }
    }
    return null;
  }, [item?.id, topicInfo.id, topicInfo.label, topicTree]);
  const tags = useMemo(
    () =>
      (Array.isArray(item?.tags) ? item.tags : [])
        .map(tag => {
          if (typeof tag === 'string') {
            return tag.trim();
          }
          if (tag && typeof tag === 'object') {
            const label = tag.name || tag.label;
            return typeof label === 'string' ? label.trim() : '';
          }
          return '';
        })
        .filter(Boolean),
    [item?.tags]
  );
  const visibleTags = tags.slice(0, visibleTagLimit);
  const hiddenTagCount = Math.max(tags.length - visibleTagLimit, 0);

  const handleOpenContent = async () => {
    if (!isClickable) return;
    await dispatch(viewContentDetail(item?.id, resolvedLanguage));
    onClick();
  };

  const handleTopicNavigate = event => {
    event.stopPropagation();
    if (!topicTargetId) return;
    navigate(`/extranet/${encodeURIComponent(topicTargetId)}`);
  };

  const handleTagNavigate = (tag, event) => {
    event.stopPropagation();
    if (!tag) return;

    const searchParams = new URLSearchParams(location.search);
    const existingKeywords = searchParams.getAll('keywords[]');

    if (!existingKeywords.includes(tag)) {
      existingKeywords.push(tag);
    }

    navigate(`/search${buildSearchUrlSearch({ keywords: existingKeywords })}`);
  };

  return (
    <article
      className={`topics-page__item${isClickable ? ' topics-page__item--clickable' : ''}`}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={isClickable ? onClick : undefined}
      onKeyDown={
        isClickable
          ? e => {
              // TODO: need to adjust the drawer page first, then re-enable this again
              // if (e.key === 'Enter' || e.key === ' ') {
              //   e.preventDefault();
              //   handleOpenContent();
              // }
            }
          : undefined
      }>
      <div className="topics-page__item-head">
        <h3>{item.title}</h3>
        <div className="topics-page__item-head-meta">
          <span className="topics-page__item-date">
            {formatDate(item.date, isAdministrationContext, i18n.language)}
          </span>
          {/* {item?.id != null && String(item.id) !== '' ? (
            <Link
              to={`/extranet/content/${encodeURIComponent(String(item.id))}/edit`}
              className="topics-page__item-edit"
              aria-label={t('extranet.editContentAria')}
              onClick={handleEditNavigate}
              onKeyDown={e => e.stopPropagation()}>
              <EditIcon width={20} height={20} />
            </Link>
          ) : null} */}
        </div>
      </div>
      <div className="topics-page__item-lang">
        <GlobeIcon /> {getExtranetLanguageDisplayCode(item.language)}
      </div>
      {item.description ? <p className="topics-page__item-description">{item.description}</p> : null}
      <div className="topics-page__item-tags">
        {topicInfo.label ? (
          topicTargetId ? (
            <button
              type="button"
              className="topics-page__chip topics-page__chip--topic"
              onClick={handleTopicNavigate}
              onKeyDown={event => {
                event.stopPropagation();
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  navigate(`/extranet/${encodeURIComponent(topicTargetId)}`);
                }
              }}>
              {topicInfo.label}
            </button>
          ) : (
            <span
              className="topics-page__chip topics-page__chip--topic"
              onClick={event => event.stopPropagation()}
              onKeyDown={event => event.stopPropagation()}>
              {topicInfo.label}
            </span>
          )
        ) : null}
        {visibleTags.map((tag, index) => (
          <button
            type="button"
            className="topics-page__chip"
            key={`${item.id}-${tag}-${index}`}
            onClick={event => handleTagNavigate(tag, event)}
            onKeyDown={event => {
              event.stopPropagation();
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleTagNavigate(tag, event);
              }
            }}>
            {tag}
          </button>
        ))}
        {hiddenTagCount > 0 ? <span className="topics-page__chip">+{hiddenTagCount}</span> : null}
      </div>
    </article>
  );
};

const skeletonStyle = { backgroundColor: 'rgba(0, 0, 0, 0.1)' };

export const ContentItemSkeleton = () => {
  return (
    <article className="topics-page__item topics-page__item--skeleton">
      <div className="topics-page__item-head">
        <Skeleton variant="text" width="90%" height={26} style={skeletonStyle} />
        <span className="topics-page__item-date">
          <Skeleton variant="text" width={48} height={20} style={skeletonStyle} />
        </span>
      </div>
      <p className="topics-page__item-lang">
        <Skeleton variant="text" width={120} height={19} style={skeletonStyle} />
      </p>
      <p className="topics-page__item-description">
        <Skeleton variant="text" width="100%" height={26} style={skeletonStyle} />
        <Skeleton variant="text" width="80%" height={26} style={skeletonStyle} />
      </p>
      <div className="topics-page__item-tags">
        <Skeleton variant="rectangular" width={80} height={24} style={skeletonStyle} />
        <Skeleton variant="rectangular" width={130} height={24} style={skeletonStyle} />
        <Skeleton variant="rectangular" width={110} height={24} style={skeletonStyle} />
      </div>
    </article>
  );
};

export const ContentListSkeleton = ({ count = 5 }) => {
  return (
    <>
      {Array.from({ length: count }).map((_, index) => (
        <ContentItemSkeleton key={index} />
      ))}
    </>
  );
};
